import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AmbientMotion from "../components/AmbientMotion";
import ArticleCard from "../components/ArticleCard";
import JournalSubscribe from "../components/JournalSubscribe";
import { useMeta } from "../hooks/useMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "All", audience: null },
  { key: "grandparents", label: "Grandparents", audience: "Grandparents" },
  { key: "parents", label: "Parents", audience: "Parents" },
  { key: "little-ones", label: "Little Ones", audience: "Little Ones" },
];

export default function Journal() {
  const [params, setParams] = useSearchParams();
  const activeKey = (params.get("audience") || "all").toLowerCase();
  const active = useMemo(
    () => FILTERS.find((f) => f.key === activeKey) || FILTERS[0],
    [activeKey]
  );

  useMeta({
    title:
      active.key === "all"
        ? "The Journal"
        : `The Journal · For ${active.label}`,
    description:
      "Slow editorial essays for grandparents, parents, and the little ones whose memories are still being built.",
  });

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = active.audience
      ? `${API}/articles?audience=${encodeURIComponent(active.audience)}`
      : `${API}/articles`;
    axios
      .get(url)
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [active]);

  const selectFilter = (key) => {
    if (key === "all") {
      setParams({}, { replace: true });
    } else {
      setParams({ audience: key }, { replace: true });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <AmbientMotion />
      <Header />

      <section className="pt-36 sm:pt-40 lg:pt-44 pb-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-primary fade-up">
            The Journal
          </p>
          <h1
            data-testid="journal-heading"
            className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight fade-up fade-up-delay-1"
          >
            Slow essays for tender questions.
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed fade-up fade-up-delay-2">
            A growing collection of reflections for grandparents, parents, and
            the little ones whose memories are still being built. Pick a quiet
            corner.
          </p>

          {/* Filter tabs */}
          <div
            data-testid="journal-filters"
            className="mt-12 flex flex-wrap items-center gap-2 sm:gap-3 fade-up fade-up-delay-3"
            role="tablist"
            aria-label="Filter essays by audience"
          >
            {FILTERS.map((f) => {
              const isActive = f.key === active.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`filter-${f.key}`}
                  onClick={() => selectFilter(f.key)}
                  className={`px-5 sm:px-6 py-2.5 rounded-full text-sm tracking-wide transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "border border-border text-foreground/75 hover:text-foreground hover:bg-secondary/70"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {loading ? (
            <p className="text-sm text-muted-foreground italic font-serif">
              Gathering essays…
            </p>
          ) : articles.length === 0 ? (
            <div className="text-center py-20 sm:py-32">
              <p className="font-serif italic text-2xl text-foreground/70">
                No essays here yet.
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Try another corner of the journal.
              </p>
              <Link
                to="/journal"
                className="mt-8 inline-block text-xs uppercase tracking-[0.25em] text-primary editorial-link"
              >
                See all essays
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-x-14 lg:gap-y-20">
              {articles.map((a, i) => (
                <ArticleCard key={a.slug} article={a} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <JournalSubscribe />
      <Footer />
    </div>
  );
}
